<template>
  <!-- Exibido apenas quando não há histórico de conversa -->
  <div class="tutoria-welcome">

    <!-- Avatar + saudação -->
    <div class="tutoria-welcome-avatar">
      <TutorIASVG class="tutoria-welcome-avatar-icon" />
    </div>

    <div class="tutoria-welcome-message">
      <p class="tutoria-welcome-greeting">
        👋 Olá! Sou a <strong>CyberTutor</strong>, sua assistente de estudos com IA.
      </p>
      <p class="tutoria-welcome-description">
        Posso te ajudar a <strong>descobrir sua trilha ideal</strong>, tirar dúvidas sobre as
        aulas, recomendar conteúdos e responder perguntas sobre a plataforma.
        É só perguntar — estou aqui! 🚀
      </p>
    </div>

    <!-- Chips de sugestão — um por categoria, trocados aleatoriamente a cada abertura -->
    <div class="tutoria-suggestions">
      <p class="tutoria-suggestions-label">Experimente perguntar:</p>
      <button
        v-for="(suggestion, i) in suggestions"
        :key="i"
        class="tutoria-suggestion-chip"
        @click="$emit('select', suggestion.text)"
      >
        <span class="tutoria-suggestion-icon">{{ suggestion.icon }}</span>
        {{ suggestion.text }}
      </button>
    </div>

  </div>
</template>

<script>
import TutorIASVG from '@/components/svg/TutorIASVG.vue'

// Pool de perguntas por categoria — uma será sorteada de cada grupo a cada montagem
const SUGGESTION_POOLS = {
  // Categoria: orientação de trilha de estudos
  trilha: [
    { text: 'Quero descobrir qual trilha de estudos é ideal para mim', icon: '🗺️' },
    { text: 'Não sei por onde começar, pode me ajudar?', icon: '🧭' },
    { text: 'Me indica uma trilha de estudos', icon: '📍' },
    { text: 'Qual carreira devo seguir no digital?', icon: '🎯' },
  ],
  // Categoria: conteúdo de aula — contexto disponível (usuário está em uma aula)
  aulaContextual: [
    { text: 'Resuma essa aula para mim', icon: '📝' },
    { text: 'Do que trata este vídeo?', icon: '🎬' },
    { text: 'Me dá um exemplo prático do que foi ensinado', icon: '💡' },
    { text: 'Explique melhor o conteúdo desta aula', icon: '🔍' },
  ],
  // Categoria: conteúdo de aula — sem contexto de aula específica
  aulaGenerica: [
    { text: 'Me indica uma aula para aprender sobre tráfego pago', icon: '📝' },
    { text: 'Quais aulas eu preciso ver para entender SEO?', icon: '🎬' },
    { text: 'Me dá um exemplo prático sobre copywriting', icon: '💡' },
    { text: 'Quais são as aulas mais importantes desta trilha?', icon: '🔍' },
  ],
  // Categoria: carreira e dúvidas gerais da plataforma (FAQ)
  carreira: [
    { text: 'Quais cursos fazer para trabalhar com marketing digital?', icon: '📈' },
    { text: 'Por onde começo para me tornar um Designer?', icon: '🎨' },
    { text: 'Como faço para baixar meu certificado?', icon: '🏆' },
    { text: 'Quais formas de pagamento são aceitas?', icon: '💳' },
  ],
}

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)]
}

export default {
  name: 'TutorIAWelcome',
  components: { TutorIASVG },
  emits: ['select'],
  props: {
    inClass: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Sorteia uma sugestão de cada categoria na montagem do componente
      suggestions: [
        pickRandom(SUGGESTION_POOLS.trilha),
        pickRandom(this.inClass ? SUGGESTION_POOLS.aulaContextual : SUGGESTION_POOLS.aulaGenerica),
        pickRandom(SUGGESTION_POOLS.carreira),
      ],
    }
  },
}
</script>

<style lang="scss" scoped>
.tutoria-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem 1rem;
  gap: 1.25rem;
  text-align: center;
}

/* Avatar centralizado */
.tutoria-welcome-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.4);

  &-icon {
    width: 34px;
    height: 34px;
    filter: brightness(0) invert(1);
  }
}

/* Bloco de texto de boas-vindas */
.tutoria-welcome-message {
  max-width: 340px;
}

.tutoria-welcome-greeting {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem;
  line-height: 1.4;
}

.tutoria-welcome-description {
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

/* Área de chips de sugestão */
.tutoria-suggestions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
  max-width: 360px;
}

.tutoria-suggestions-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin: 0 0 0.25rem;
}

/* Chip clicável — preenche o input ao clicar */
.tutoria-suggestion-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.8125rem;
  color: #334155;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
  line-height: 1.4;

  &:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.tutoria-suggestion-icon {
  font-size: 1rem;
  flex-shrink: 0;
}
</style>
